import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { Navbar } from '../components/Navbar';
import {
  Users, Play, Bell, BookmarkPlus, Radio, Clock, Eye,
  RefreshCw, Shield, TrendingUp, Trophy, Zap, BarChart2,
  UserCheck, CreditCard, History, ArrowLeft, Download, FileText
} from 'lucide-react';
import { exportCSV, exportPDF } from '../utils/exportReports';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { label: 'Today', days: 1 },
  { label: '7 Days', days: 7 },
  { label: '30 Days', days: 30 },
];

const EVENT_BADGE = {
  video_play:         { label: 'Play',       cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  reminder_set:       { label: 'Reminder',   cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  reminder_triggered: { label: 'Email Sent', cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  mylist_add:         { label: 'My List',    cls: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  login:              { label: 'Login',      cls: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
};

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toDateString() === new Date().toDateString()
      ? `Today ${fmtTime(iso)}`
      : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${fmtTime(iso)}`;
  } catch { return iso; }
};

// Shared dark tooltip for Recharts
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 shadow-2xl">
      <p className="text-white/50 text-xs mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: entry.color }} />
          <span className="text-white/60 text-xs">{entry.name}:</span>
          <span className="text-white text-xs font-bold">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

function SectionLabel({ children }) {
  return <p className="text-white/40 text-[11px] font-bold uppercase tracking-[0.15em] mb-3">{children}</p>;
}

function Card({ icon: Icon, label, value, sub, iconBg = 'bg-emerald-500/15', iconColor = 'text-emerald-400', testId }) {
  return (
    <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-5 flex items-start gap-4 hover:border-white/15 transition-all group">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${iconBg} group-hover:scale-105 transition-transform`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-white/45 text-[11px] uppercase tracking-wider mb-1 truncate">{label}</p>
        <p className="text-white font-bold text-2xl leading-tight tabular-nums" data-testid={testId}>{value ?? '—'}</p>
        {sub && <p className="text-white/30 text-xs mt-1 truncate">{sub}</p>}
      </div>
    </div>
  );
}

function BigStatCard({ label, value, icon: Icon, border, sub }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-6 border ${border} bg-[#0f0f0f]`}>
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, white 0%, transparent 70%)' }} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-white/50 text-xs uppercase tracking-widest mb-2">{label}</p>
          <p className="text-white font-black text-4xl tabular-nums leading-none">{value ?? '—'}</p>
          {sub && <p className="text-white/40 text-xs mt-2">{sub}</p>}
        </div>
        <Icon className="w-8 h-8 text-white/15 shrink-0" />
      </div>
    </div>
  );
}

const CHART_GRID = { stroke: 'rgba(255,255,255,0.04)', strokeDasharray: '3 3' };
const AXIS_TICK = { fill: 'rgba(255,255,255,0.3)', fontSize: 11 };

function ChartCard({ title, sub, children, testId }) {
  return (
    <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-6" data-testid={testId}>
      <div className="mb-5">
        <p className="text-white font-semibold text-sm">{title}</p>
        {sub && <p className="text-white/35 text-xs mt-0.5">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

export default function AdminMetrics() {
  const [selectedDays, setSelectedDays] = useState(1);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const fetchMetrics = useCallback(async (days = selectedDays) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/metrics?days=${days}`, {
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) { setError('admin_only'); setLoading(false); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMetrics(await res.json());
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDays]);

  useEffect(() => { fetchMetrics(selectedDays); }, [selectedDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 60s
  useEffect(() => {
    const t = setInterval(() => fetchMetrics(selectedDays), 60000);
    return () => clearInterval(t);
  }, [selectedDays, fetchMetrics]);

  if (error === 'admin_only') return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-center px-4">
        <Shield className="w-16 h-16 text-white/15" />
        <h1 className="text-white text-2xl font-bold">Admin Access Required</h1>
        <p className="text-white/40 max-w-sm text-sm">This page is restricted to platform administrators.</p>
        <button onClick={() => navigate('/')} className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-full text-sm font-semibold hover:bg-emerald-700 transition-colors">Go Home</button>
      </div>
    </div>
  );

  const m = metrics;
  const periodLabel = PERIOD_OPTIONS.find(o => o.days === selectedDays)?.label ?? 'Today';

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="pt-20 pb-20 px-4 sm:px-6 max-w-7xl mx-auto">

        {/* ── Header ───────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Link to="/admin" className="text-white/30 hover:text-white/60 transition-colors" data-testid="back-to-admin">
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <span className="text-white/25 text-sm">Admin / Metrics</span>
            </div>
            <h1 className="text-white text-3xl font-black tracking-tight">Network Dashboard</h1>
            <p className="text-white/30 text-sm mt-0.5">
              {lastRefresh ? `Updated ${fmtTime(lastRefresh.toISOString())} · auto-refresh 60s` : 'Loading…'}
            </p>
          </div>

          {/* Filter + Export + Refresh */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <div className="flex bg-[#1a1a1a] border border-white/8 rounded-full p-1 gap-1" data-testid="period-filter">
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.days}
                  onClick={() => setSelectedDays(opt.days)}
                  data-testid={`filter-${opt.days}d`}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    selectedDays === opt.days
                      ? 'bg-emerald-600 text-white shadow'
                      : 'text-white/45 hover:text-white/70'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Export CSV */}
            <button
              onClick={() => exportCSV(metrics, selectedDays)}
              disabled={!metrics}
              data-testid="export-csv"
              title="Export CSV"
              className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/8 rounded-full text-white/50 hover:text-emerald-400 hover:border-emerald-500/40 hover:bg-emerald-500/5 text-xs font-medium transition-all"
            >
              <FileText className="w-3.5 h-3.5" />
              CSV
            </button>

            {/* Export PDF */}
            <button
              onClick={() => exportPDF(metrics, selectedDays, setExporting)}
              disabled={!metrics || exporting}
              data-testid="export-pdf"
              title="Export branded PDF report"
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600/10 border border-emerald-500/30 rounded-full text-emerald-400 hover:bg-emerald-600/20 hover:border-emerald-500/60 text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Download className="w-3.5 h-3.5" />
              }
              {exporting ? 'Building…' : 'PDF'}
            </button>

            <button
              onClick={() => fetchMetrics(selectedDays)}
              disabled={loading}
              data-testid="refresh-metrics"
              className="p-2 bg-white/5 border border-white/8 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {loading && !m ? (
          <div className="flex items-center justify-center h-64 gap-3 text-white/25">
            <RefreshCw className="w-5 h-5 animate-spin text-emerald-500" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-400 text-sm">Failed to load: {error}</div>
        ) : m && (
          <div className="space-y-10">

            {/* ── Big 4 ───────────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="big-stats">
              <BigStatCard label="Total Users" value={m.users.total} icon={Users}
                border="border-emerald-500/20" sub="registered" />
              <BigStatCard label={`Active (${periodLabel})`} value={m.users.active_in_period} icon={Eye}
                border="border-blue-500/20" sub="played a video" />
              <BigStatCard label="Live Now" value={m.retention.live_viewers} icon={Radio}
                border="border-red-500/20" sub="watching live" />
              <BigStatCard label="Subscribed" value={m.users.subscribed} icon={CreditCard}
                border="border-purple-500/20" sub={`${m.users.subscription_rate} of users`} />
            </div>

            {/* ── Trends Section ───────────────────────── */}
            <section data-testid="trends-section">
              <SectionLabel>Growth Trends — {periodLabel}</SectionLabel>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* Viewer Growth */}
                <div className="lg:col-span-2">
                  <ChartCard
                    title="Viewer Growth"
                    sub="Unique viewers and total plays over time"
                    testId="chart-viewer-growth"
                  >
                    <div id="chart-export-viewer-growth">
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={m.trends.viewer_growth} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                        <defs>
                          <linearGradient id="gradViewers" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradPlays" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid {...CHART_GRID} />
                        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false}
                          interval={selectedDays === 30 ? 4 : selectedDays === 7 ? 0 : 3} />
                        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                        <Tooltip content={<DarkTooltip />} />
                        <Legend wrapperStyle={{ paddingTop: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }} />
                        <Area type="monotone" dataKey="viewers" name="Viewers"
                          stroke="#10b981" strokeWidth={2} fill="url(#gradViewers)" dot={false} />
                        <Area type="monotone" dataKey="plays" name="Plays"
                          stroke="#3b82f6" strokeWidth={2} fill="url(#gradPlays)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>

                {/* Peak Viewing Hours */}
                <ChartCard
                  title="Peak Viewing Hours"
                  sub="Play count by hour of day"
                  testId="chart-peak-hours"
                >
                  <div id="chart-export-peak-hours">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={m.trends.peak_hours} margin={{ top: 5, right: 5, bottom: 0, left: -25 }}>
                      <CartesianGrid {...CHART_GRID} vertical={false} />
                      <XAxis dataKey="hour" tick={{ ...AXIS_TICK, fontSize: 9 }} tickLine={false} axisLine={false}
                        interval={3} tickFormatter={h => h.replace(':00', '')} />
                      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<DarkTooltip />} />
                      <Bar dataKey="plays" name="Plays" fill="#10b981" fillOpacity={0.7}
                        radius={[3, 3, 0, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                    </div>

                </ChartCard>

              </div>

              {/* Retention Trend */}
              <div className="mt-4">
                <ChartCard
                  title="Retention Trend"
                  sub="Reminders set and My List adds over time — engagement signals"
                  testId="chart-retention"
                >
                  <div id="chart-export-retention">
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={m.trends.retention_trend} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                      <CartesianGrid {...CHART_GRID} />
                      <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false}
                        interval={selectedDays === 30 ? 4 : selectedDays === 7 ? 0 : 3} />
                      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<DarkTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: '8px', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }} />
                      <Line type="monotone" dataKey="reminders" name="Reminders"
                        stroke="#3b82f6" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="mylist" name="My List"
                        stroke="#a855f7" strokeWidth={2} dot={false} strokeDasharray="5 3" />
                    </LineChart>
                  </ResponsiveContainer>
                  </div>

                </ChartCard>
              </div>
            </section>

            {/* ── Users Section ────────────────────────── */}
            <section data-testid="users-section">
              <SectionLabel>Users</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Card icon={Users} label="Total" value={m.users.total} testId="metric-total" />
                <Card icon={UserCheck} label={`New (${periodLabel})`} value={m.users.new_in_period}
                  iconBg="bg-cyan-500/15" iconColor="text-cyan-400" />
                <Card icon={Eye} label={`Active (${periodLabel})`} value={m.users.active_in_period}
                  iconBg="bg-blue-500/15" iconColor="text-blue-400" sub="played video" />
                <Card icon={CreditCard} label="Subscribed" value={m.users.subscribed}
                  iconBg="bg-purple-500/15" iconColor="text-purple-400" />
                <Card icon={TrendingUp} label="Sub Rate" value={m.users.subscription_rate}
                  iconBg="bg-amber-500/15" iconColor="text-amber-400" sub="of total" />
              </div>
            </section>

            {/* ── Watching Behavior ────────────────────── */}
            <section data-testid="watching-section">
              <SectionLabel>Watching Behavior — {periodLabel}</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card icon={Play} label="Plays" value={m.watching.video_plays} />
                <Card icon={Users} label="Unique Viewers" value={m.watching.unique_viewers}
                  iconBg="bg-teal-500/15" iconColor="text-teal-400" />
                <Card icon={Clock} label="Avg Watch Time" value={m.watching.avg_watch_time}
                  iconBg="bg-orange-500/15" iconColor="text-orange-400" sub="est. from seek" />
                <Card icon={History} label="Continue Watching" value={m.watching.continue_watching_count}
                  iconBg="bg-indigo-500/15" iconColor="text-indigo-400" sub="resumed users" />
              </div>
            </section>

            {/* ── Live Activity ─────────────────────────── */}
            <section data-testid="live-section">
              <SectionLabel>Live Activity</SectionLabel>
              <div className="bg-[#141414] border border-white/[0.07] rounded-2xl overflow-hidden">
                {m.live_viewers_per_match.length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <Radio className="w-7 h-7 text-white/15 mx-auto mb-2" />
                    <p className="text-white/25 text-sm">No live matches right now</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead><tr className="border-b border-white/[0.07]">
                      <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Match</th>
                      <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Competition</th>
                      <th className="text-right px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Viewers Now</th>
                    </tr></thead>
                    <tbody>
                      {m.live_viewers_per_match.map(item => (
                        <tr key={item.match_id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-3.5 text-white text-sm font-medium flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />{item.title}
                          </td>
                          <td className="px-5 py-3.5 text-white/45 text-sm">{item.competition || '—'}</td>
                          <td className="px-5 py-3.5 text-right text-emerald-400 font-bold tabular-nums">{item.viewers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* ── Competition Intelligence ──────────────── */}
            <section data-testid="competition-section">
              <SectionLabel>Competition Intelligence (7 days)</SectionLabel>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1">
                  {m.most_watched_competition ? (
                    <div className="h-full bg-gradient-to-br from-emerald-950/60 to-[#141414] border border-emerald-500/20 rounded-2xl p-6 flex flex-col justify-between" data-testid="most-watched-comp">
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <Trophy className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400 text-xs font-bold uppercase tracking-wide">Most Watched</span>
                        </div>
                        <p className="text-white font-black text-xl leading-tight">{m.most_watched_competition.competition}</p>
                      </div>
                      <div className="flex items-end justify-between mt-6">
                        <div><p className="text-white/40 text-xs mb-0.5">Plays</p><p className="text-white font-black text-3xl tabular-nums">{m.most_watched_competition.plays}</p></div>
                        <div className="text-right"><p className="text-white/40 text-xs mb-0.5">Viewers</p><p className="text-white font-bold text-xl tabular-nums">{m.most_watched_competition.unique_viewers}</p></div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full bg-[#141414] border border-white/[0.07] rounded-2xl p-6 flex items-center justify-center">
                      <p className="text-white/20 text-sm text-center">No competition data yet</p>
                    </div>
                  )}
                </div>
                <div className="lg:col-span-2 bg-[#141414] border border-white/[0.07] rounded-2xl overflow-hidden" data-testid="competition-table">
                  {m.competition_watch.length === 0 ? (
                    <div className="px-6 py-10 text-center text-white/25 text-sm">No data available</div>
                  ) : (
                    <table className="w-full">
                      <thead><tr className="border-b border-white/[0.07]">
                        <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Competition</th>
                        <th className="text-right px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Plays</th>
                        <th className="text-right px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Viewers</th>
                      </tr></thead>
                      <tbody>
                        {m.competition_watch.map((item, i) => {
                          const max = m.competition_watch[0]?.plays || 1;
                          return (
                            <tr key={item.competition} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-3">
                                  <span className="text-white/20 text-xs font-mono w-4 shrink-0">{i + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm font-medium truncate">{item.competition}</p>
                                    <div className="mt-1 h-1 bg-white/5 rounded-full overflow-hidden">
                                      <div className="h-full bg-emerald-500/50 rounded-full" style={{ width: `${Math.max(4, (item.plays / max) * 100)}%` }} />
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-3 text-right text-white font-bold text-sm tabular-nums">{item.plays}</td>
                              <td className="px-5 py-3 text-right text-white/50 text-sm tabular-nums">{item.unique_viewers}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </section>

            {/* ── Retention + Conversion ───────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section data-testid="retention-section">
                <SectionLabel>Retention Signals — {periodLabel}</SectionLabel>
                <div className="grid grid-cols-3 gap-3">
                  <Card icon={Bell} label="Reminders" value={m.retention.reminders}
                    iconBg="bg-yellow-500/15" iconColor="text-yellow-400" sub="set" />
                  <Card icon={BookmarkPlus} label="My List Adds" value={m.retention.mylist_adds}
                    iconBg="bg-purple-500/15" iconColor="text-purple-400" />
                  <Card icon={Zap} label="Live Now" value={m.retention.live_viewers}
                    iconBg="bg-red-500/15" iconColor="text-red-400" sub="last 5 min" />
                </div>
              </section>
              <section data-testid="conversion-section">
                <SectionLabel>Conversion</SectionLabel>
                <div className="grid grid-cols-3 gap-3">
                  <Card icon={UserCheck} label="Signups" value={m.conversion.signup_today}
                    iconBg="bg-cyan-500/15" iconColor="text-cyan-400" sub={periodLabel} />
                  <Card icon={CreditCard} label="Subscribed" value={m.conversion.subscribed_users}
                    iconBg="bg-purple-500/15" iconColor="text-purple-400" />
                  <div className="bg-[#141414] border border-dashed border-white/[0.08] rounded-2xl p-4 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-white/20 text-[10px] uppercase tracking-wide">Paid Rate</p>
                      <p className="text-white/40 font-bold text-lg mt-1">{m.users.subscription_rate}</p>
                      <p className="text-white/15 text-[9px] mt-0.5">future ready</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* ── Top Content ──────────────────────────── */}
            <section>
              <SectionLabel>Top Content — {periodLabel}</SectionLabel>
              <div className="bg-[#141414] border border-white/[0.07] rounded-2xl overflow-hidden" data-testid="top-content-table">
                {m.top_content.length === 0 ? (
                  <div className="px-6 py-10 text-center text-white/25 text-sm">No plays in this period</div>
                ) : (
                  <table className="w-full">
                    <thead><tr className="border-b border-white/[0.07]">
                      <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold w-8">#</th>
                      <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Match</th>
                      <th className="text-right px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Plays</th>
                    </tr></thead>
                    <tbody>
                      {m.top_content.map((item, i) => {
                        const max = m.top_content[0]?.play_count || 1;
                        return (
                          <tr key={item.content_id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                            <td className="px-5 py-3.5 text-white/25 text-sm font-mono">{i + 1}</td>
                            <td className="px-5 py-3.5 text-white text-sm font-medium">{item.title}</td>
                            <td className="px-5 py-3.5 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <div className="h-1 bg-white/5 rounded-full overflow-hidden w-20 hidden sm:block">
                                  <div className="h-full bg-emerald-500/50 rounded-full" style={{ width: `${(item.play_count / max) * 100}%` }} />
                                </div>
                                <span className="text-emerald-400 font-bold text-sm tabular-nums w-5 text-right">{item.play_count}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* ── Event Stream ─────────────────────────── */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <SectionLabel>Event Stream</SectionLabel>
                <span className="text-white/20 text-xs -mt-3">last 20</span>
              </div>
              <div className="bg-[#141414] border border-white/[0.07] rounded-2xl overflow-hidden" data-testid="recent-events-table">
                {m.recent_events.length === 0 ? (
                  <div className="px-6 py-10 text-center text-white/25 text-sm">No events yet</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px]">
                      <thead><tr className="border-b border-white/[0.07]">
                        <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">User</th>
                        <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Event</th>
                        <th className="text-left px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Match</th>
                        <th className="text-right px-5 py-3 text-white/35 text-[11px] uppercase tracking-wide font-semibold">Time</th>
                      </tr></thead>
                      <tbody>
                        {m.recent_events.map((ev, i) => {
                          const badge = EVENT_BADGE[ev.event_type] || { label: ev.event_type, cls: 'bg-white/10 text-white/40 border-white/10' };
                          return (
                            <tr key={ev.id || i} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                              <td className="px-5 py-3 text-white/60 text-sm max-w-[160px] truncate">{ev.user}</td>
                              <td className="px-5 py-3">
                                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${badge.cls}`}>{badge.label}</span>
                              </td>
                              <td className="px-5 py-3 text-white/40 text-sm max-w-[180px] truncate">{ev.match || '—'}</td>
                              <td className="px-5 py-3 text-right text-white/30 text-xs tabular-nums whitespace-nowrap">{fmtDate(ev.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>

          </div>
        )}
      </div>
    </div>
  );
}
